// src/views/admin/offers/component/AddEditItemModel.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';

import { Input } from '@/components/ui/inputs/input';
import InputRadio from '@/components/ui/inputs/input-radio';
import { InputSelect } from '@/components/ui/inputs/input-select';
import CommonModal from '@/components/ui/modals/CommonModal';

import { addOfferItemService, updateOfferItemService, getByIdOfferItemService } from '@/services/OfferItemServices';
import { getItemService } from '@/services/ItemServices';
import validationErrors from '@/lib/validationErrors';
import ToastMe from '@/components/ui/ToastMe';

const AddEditItemModel = ({ modelTitle, openModel, setOpenModel, getTableData, offerId, editDataId = '' }) => {
  const selectRef = useRef(null);
  const dispatch = useDispatch();

  // Tenant (same pattern as your other forms)
  const companyFromStore = useSelector((s) => s.Auth?.data?.data?.company_id);
  const branchFromStore  = useSelector((s) => s.Branch?.active?._id);
  const company_id = companyFromStore || (typeof localStorage !== 'undefined' ? localStorage.getItem('company_id') : '');
  const branch_id  = branchFromStore  || (typeof localStorage !== 'undefined' ? localStorage.getItem('branch_id')  : '');

  const {
    setValue,
    getValues,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      item_id: '',   // keep string id here
      status: '1',   // radios as strings
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFormSumbmit, setIsFormSumbmit] = useState(false);
  const [itemOptions, setItemOptions] = useState([]);
  const [itemStatus, setItemStatus] = useState(1);

  useEffect(() => {
    if (!openModel) return;
    formReset();
    getItemsData();
    if (editDataId) getDataById(editDataId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModel, editDataId]);

  const formReset = async () => {
    reset({ item_id: '', status: '1' });
    setItemStatus(1);
  };

  const handleModelClose = () => {
    formReset();
    setOpenModel(false);
    document.body.style.overflowY = 'auto';
    getTableData?.();
  };

  const handleChangeRadio = (event) => {
    if (event?.target?.name === 'status') {
      const val = String(event.target.value);
      setValue('status', val);
      setItemStatus(Number(val));
    }
  };

  const getItemsData = () => {
    setIsLoading(true);
    dispatch(getItemService({ search: '', page: 1, limit: 1000 }))
      .then((res) => {
        const rows = res?.data?.data?.data || [];
        setItemOptions(
          rows.map((el) => ({ value: el?._id, label: el?.name }))
        );
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const getDataById = (id) => {
    setIsLoading(true);
    dispatch(getByIdOfferItemService(id))
      .then((res) => {
        const d = res?.data?.data || {};
        // form keeps just the string id
        setValue('item_id', d?.item_id?._id || d?.item_id || '');
        setValue('status', String(d?.status ?? '1'));
        setItemStatus(Number(d?.status ?? 1));
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const onSubmit = (data) => {
    setIsFormSumbmit(true);

    if (!offerId) {
      ToastMe('Missing offer id.', 'error');
      setIsFormSumbmit(false);
      return;
    }
    if (!data.item_id) {
      ToastMe('Please select an item.', 'error');
      setIsFormSumbmit(false);
      return;
    }

    // Normalize & include tenant
    const payload = {
      offer_id: offerId,
      item_id: typeof data.item_id === 'object' ? data.item_id?.value : data.item_id, // safety
      status: Number(data.status ?? 1),
      // pass tenant explicitly (plugin should also set, but this makes it robust)
      company_id: company_id || undefined,
      ...(branch_id && String(branch_id).toLowerCase() !== 'all' ? { branch_id } : {}),
    };

    const thunk = editDataId
      ? updateOfferItemService(editDataId, payload)
      : addOfferItemService(payload);

    dispatch(thunk)
      .then(() => {
        formReset();
        setIsFormSumbmit(false);
        handleModelClose();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsFormSumbmit(false);
      });
  };

  return (
    <CommonModal modelTitle={modelTitle} openModel={openModel} handleModelClose={handleModelClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Item (store only id in the form) */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="item_id"
            render={({ field }) => (
              <InputSelect
                ref={selectRef}
                id="item_id"
                labelText="Item"
                labelStyleClassName="db-field-title required"
                // Convert stored id -> option object for the Select
                value={itemOptions.find((opt) => opt.value === field.value) || null}
                onChange={(opt) => field.onChange(opt?.value ?? '')}
                options={itemOptions}
                errorType={errors?.item_id?.type}
              />
            )}
          />

          {/* Status */}
          <div className="form-input-item">
            <label className="db-field-title required" htmlFor="active">Status</label>
            <div className="db-field-radio-group">
              <InputRadio
                id="active"
                name="status"
                labelText="Active"
                value="1"
                onChange={handleChangeRadio}
                checked={itemStatus === 1}
              />
              <InputRadio
                id="inactive"
                name="status"
                labelText="Inactive"
                value="2"
                onChange={handleChangeRadio}
                checked={itemStatus === 2}
              />
            </div>
          </div>
        </div>

        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="submit"
              className="db-btn py-2 text-white bg-primary"
              disabled={isFormSumbmit || isLoading}
            >
              <i className="lab lab-save"></i>
              <span>save</span>
              {isFormSumbmit && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </button>
            <button type="button" className="modal-btn-outline modal-close" onClick={handleModelClose}>
              <i className="lab lab-close"></i>
              <span>close</span>
            </button>
          </div>
        </div>
      </form>
    </CommonModal>
  );
};

export default AddEditItemModel;
